import aa from "search-insights";

type AlgoliaInsightsHit = {
  objectID: string;
  title?: string | null;
  __indexName?: string | null;
  __queryID?: string | null;
  __position?: number | null;
};

type TrackAlgoliaSearchSelectionParams = {
  hit: AlgoliaInsightsHit;
  source: string;
  contentType: "Devotional" | "Series";
  fallbackIndexName?: string;
  userToken?: string;
};

type TrackDevotionalSearchSelectionParams = {
  hit: AlgoliaInsightsHit;
  source: string;
  userToken?: string;
};

let hasInitializedInsightsClient = false;

export function initializeAlgoliaInsightsClient() {
  if (hasInitializedInsightsClient || typeof window === "undefined") {
    return;
  }

  aa("init", {
    appId: import.meta.env.VITE_ALGOLIA_APP_ID,
    apiKey: import.meta.env.VITE_ALGOLIA_SEARCH_KEY,
    anonymousUserToken: true,
    useCookie: true,
  });
  aa("addAlgoliaAgent", "worship-devo-app");

  hasInitializedInsightsClient = true;
}

function buildInsightsHeaders(hit: AlgoliaInsightsHit, input: { source: string; contentType: string }) {
  return {
    referrer: input.source,
    title: hit.title ?? "",
    type: input.contentType,
  };
}

export function trackAlgoliaSearchSelection({
  hit,
  source,
  contentType,
  fallbackIndexName,
  userToken,
}: TrackAlgoliaSearchSelectionParams) {
  if (!hit.objectID) {
    return;
  }

  const indexName = hit.__indexName ?? fallbackIndexName;
  if (!indexName) {
    return;
  }

  const headers = buildInsightsHeaders(hit, { source, contentType });
  const sharedEventPayload = {
    index: indexName,
    ...(userToken ? { userToken } : {}),
  };

  const eventNamePrefix = contentType;
  const queryID = hit.__queryID ?? null;
  const position = hit.__position ?? null;

  if (typeof queryID === "string" && typeof position === "number") {
    const afterSearchPayload = {
      ...sharedEventPayload,
      queryID,
      objectIDs: [hit.objectID],
      positions: [position],
    };

    void aa(
      "clickedObjectIDsAfterSearch",
      {
        eventName: `${eventNamePrefix} - Click`,
        ...afterSearchPayload,
      },
      { headers },
    );
    void aa(
      "convertedObjectIDsAfterSearch",
      {
        eventName: `${eventNamePrefix} - Conversion`,
        ...afterSearchPayload,
      },
      { headers },
    );

    return;
  }

  const basicPayload = {
    ...sharedEventPayload,
    objectIDs: [hit.objectID],
  };

  void aa(
    "clickedObjectIDs",
    {
      eventName: `${eventNamePrefix} - Click`,
      ...basicPayload,
    },
    { headers },
  );
  void aa(
    "convertedObjectIDs",
    {
      eventName: `${eventNamePrefix} - Conversion`,
      ...basicPayload,
    },
    { headers },
  );
}

export function trackDevotionalSearchSelection({ hit, source, userToken }: TrackDevotionalSearchSelectionParams) {
  trackAlgoliaSearchSelection({
    hit,
    source,
    contentType: "Devotional",
    fallbackIndexName: import.meta.env.VITE_ALGOLIA_DEVO_INDEX,
    userToken,
  });
}
